import { ReactNode, useEffect, useState } from 'react';
import { leagueGroupName } from 'helpers/leagueGroupName';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { getLeaguesForSidebar, getSports } from 'services/api/sportBetsService';
import { sportTranslationKey } from 'helpers/sportI18n';
import { getDefaultSportIconSrc } from 'helpers/sportIcon';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { CountryFlag } from 'components/TeamCrest/TeamCrest';
import type { Sport } from 'types/Sport';
import styles from './ModernSportSheet.module.css';

type Tab = 'sport' | 'category' | 'tournament';

type Row = { id: number; name: string; count: number; cc?: string };
type Group = Row & { subList: Row[]; groupCode?: string };

type Props = {
  close: () => void;
  sportId?: number;
  leagueId?: number;
};

/** Modern layout: the offer picker sliding up from the bottom - sport, then
 *  country (or other group), then tournament. */
export const ModernSportSheet = ({ close, sportId, leagueId }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [sports, setSports] = useState<Sport[]>([]);
  const [sport, setSport] = useState<Sport | null>(null);
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [group, setGroup] = useState<Group | null>(null);
  const [tab, setTab] = useState<Tab>('category');

  useEffect(() => {
    let live = true;
    getSports(undefined)
      .catch(() => [])
      .then((s) => {
        if (!live) return;
        const list: Sport[] = Array.isArray(s) ? s : [];
        setSports(list);
        const current = list.find((x) => x.id === sportId) ?? list[0] ?? null;
        setSport(current);
        if (!current) setTab('sport');
      });
    return () => {
      live = false;
    };
  }, [sportId]);

  useEffect(() => {
    if (!sport) return;
    let live = true;
    setGroups(null);
    getLeaguesForSidebar(sport.id)
      .catch(() => null)
      .then((data: any[] | null) => {
        if (!live) return;
        const list: Group[] = (data ?? []).map((g: any) =>
          Array.isArray(g?.subList)
            ? g
            : { id: g.id, name: g.name, count: g.count, cc: g.cc, subList: [g] }
        );
        setGroups(list);
        const mine = list.find((g) => g.subList.some((l) => l.id === leagueId));
        setGroup((prev) => prev ?? mine ?? null);
      });
    return () => {
      live = false;
    };
  }, [sport, leagueId]);

  // Esc closes; the page behind does not scroll.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [close]);

  const sportIcon = (id: number) => (
    <SportIcon
      sportId={id}
      className={styles.rowIcon}
      onError={(e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = getDefaultSportIconSrc();
      }}
    />
  );

  const row = (
    key: number,
    icon: ReactNode,
    name: string,
    count: number,
    selected: boolean,
    onClick: () => void
  ) => (
    <button
      type="button"
      key={key}
      className={classNames(styles.row, selected && styles.rowSelected)}
      onClick={onClick}
    >
      {icon}
      <span className={styles.rowName}>{name}</span>
      <span className={styles.rowCount}>{count}</span>
      <span className={selected ? styles.check : styles.chev} aria-hidden>
        {selected ? '✓' : '›'}
      </span>
    </button>
  );

  const sportName = (s: Sport) =>
    t(sportTranslationKey(s.id), { defaultValue: s.name });

  const tabs: { key: Tab; label: string; value: string }[] = [
    {
      key: 'sport',
      label: t('sheet_sport'),
      value: sport ? sportName(sport) : '',
    },
    {
      key: 'category',
      label: t('sheet_category'),
      value: group
        ? leagueGroupName(group.name, group.groupCode, i18n.language)
        : t('sheet_choose_category'),
    },
    {
      key: 'tournament',
      label: t('sheet_tournament'),
      value:
        group?.subList.find((l) => l.id === leagueId)?.name ??
        t('sheet_choose_tournament'),
    },
  ];

  return createPortal(
    <div className={styles.root}>
      <div className={styles.scrim} onClick={close} />
      <div className={styles.sheet} role="dialog" aria-modal="true">
        <button
          type="button"
          className={styles.handle}
          aria-label={t('close_label')}
          onClick={close}
        />
        <div className={styles.tabs}>
          {tabs.map((x) => (
            <button
              type="button"
              key={x.key}
              className={classNames(styles.tab, tab === x.key && styles.tabOn)}
              disabled={
                (x.key === 'category' && !sport) ||
                (x.key === 'tournament' && !group)
              }
              onClick={() => setTab(x.key)}
            >
              <strong>{x.label}</strong>
              <small>{x.value}</small>
            </button>
          ))}
        </div>
        <div className={styles.list}>
          {tab === 'sport' &&
            sports.map((s) =>
              row(
                s.id,
                sportIcon(s.id),
                sportName(s),
                s.count ?? 0,
                s.id === sport?.id,
                () => {
                  if (s.id !== sport?.id) {
                    setSport(s);
                    setGroup(null);
                  }
                  setTab('category');
                }
              )
            )}
          {tab === 'category' &&
            sport &&
            (groups ?? []).map((g) =>
              row(
                g.id,
                g.cc ? (
                  <CountryFlag cc={g.cc} className={styles.rowFlag} />
                ) : (
                  sportIcon(sport.id)
                ),
                leagueGroupName(g.name, g.groupCode, i18n.language),
                g.count,
                g.id === group?.id,
                () => {
                  setGroup(g);
                  setTab('tournament');
                }
              )
            )}
          {tab === 'tournament' &&
            sport &&
            group?.subList.map((l) =>
              row(
                l.id,
                l.cc ? (
                  <CountryFlag cc={l.cc} className={styles.rowFlag} />
                ) : (
                  sportIcon(sport.id)
                ),
                l.name,
                l.count,
                l.id === leagueId,
                () => {
                  close();
                  navigate(
                    `/sports-betting/${sport.name}/${sport.id}/${l.name}/${l.id}`
                  );
                }
              )
            )}
          {tab !== 'sport' && groups === null && (
            <div className={styles.loading} />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
